/**
 * L2 chrome — PageShell · ItemPage · SessionPage. Generic, like the
 * collections: sealing the page lifecycle is a capability every widget reuses,
 * not a domain.
 *
 * SEALED on all three: the HOST owns the bezel, the nav pill, the capture button and the
 * back chevron. A widget page never draws them — that is the single rule that
 * keeps every widget feeling like part of the app instead of a website inside
 * it.
 *
 * PageShell also carries the required iPhone large-title head (eyebrow +
 * title) and the live-update subscription: a feed push re-renders IN PLACE, so
 * scroll, filters and sheet state survive.
 *
 * The two detail pages share ONE declarative section grammar
 * (hero · carousel · gallery · kv · action, plus the `node` passthrough for
 * everything else) rather than a per-widget Detail component. What differs is
 * only what they are bound to: ItemPage
 * focuses an ITEM (page_args.item_id → focusEntry), SessionPage focuses a
 * CAPTURE (page_args.session_id → focusGrid) — the same identity split the
 * grids make.
 */
import { useContext, useEffect, useState } from 'react';
import { PageWithHeader } from './pageHeader';
import { PageHeaderHostContext, usesPageHeader } from '../pageHeaderContext';
import { resetButton, hitTarget, kvLines, waitsForData } from './ui';
import { Image, Padding } from './primitives';
import { useViewIR } from '../viewIRContext';
import { bodyHorizontalInset, contentPadding } from '../layout';
import { resolveProps } from '../props';
import { Carousel, MediaPicker } from './media';
import { EmptyState, SkeletonText } from './feedback';
import { UnsupportedComponent } from './Unsupported';
import { familyOf } from '../families';
import { contractLifecycle } from '../paths';

const LAYOUT_GAP = {
  dashboard: 'var(--sp-5)',
  single: 'var(--sp-4)',
  detail: 'var(--sp-4)',
  'full-bleed': '0',
};

/** The two regions the shell owns itself, and therefore never lays out inline. */
const SHELL_REGIONS = new Set(['actions', 'actionBar']);

/**
 * PANELS — the dashboard's elevated cards.
 *
 * A dashboard is a STACK OF MODULES, and a module reads as a module by sitting
 * on its own raised surface. The alternative — every module drawn flat on the
 * page background — is what the kit did, and it made a ring, a tile row and a
 * chart look like one undifferentiated column of ink.
 *
 * The gate is the registry FAMILY, not a list of type names, so a component
 * added to the registry lands in the right layer without this file being edited
 * to know about it:
 *   dataviz · content   a module — it gets a panel
 *   collection          NEVER: its rows are already Cards, so a panel would be
 *                       a card holding cards
 *   control · status · chrome · layout · media · host
 *                       NEVER: a button is a button, an empty state is
 *                       full-bleed, a mode switch is the page's own tab bar, and
 *                       a layout container is whatever its children are
 *
 * Two carve-outs, both about the same thing — a panel is for a MODULE, and
 * these are not modules:
 *   · LABEL types (a heading, a line of copy, a badge). A section heading
 *     belongs ON the backdrop, naming the panel under it; boxed, it detaches
 *     from the thing it names.
 *   · `props.bare: true` — the author's own opt-out, for the module that reads
 *     better full-bleed (a tone-plated notice list already carries its own
 *     surface).
 *
 * DASHBOARD ONLY. A detail page is one continuous subject, not a set of
 * modules, so panelling its sections would fragment one reading into five.
 */
const PANEL_FAMILIES = new Set(['dataviz', 'content']);

const LABEL_TYPES = new Set(['Text', 'Icon', 'Badge', 'Monogram', 'SectionHeader']);

function wantsPanel(item) {
  if (!item?.type || item.bare) return false;
  if (LABEL_TYPES.has(item.type)) return false;
  return PANEL_FAMILIES.has(familyOf(item.type));
}

/**
 * One elevated module surface.
 *
 * `vi-native-panel` is the class the kit stylesheet hides when it is EMPTY: a
 * data-gated module (a notice list with nothing to warn about, a first-run
 * empty that suppressed itself) renders null, and a panel drawn around nothing
 * is a floating white box promising content that does not exist.
 */
function Panel({ children }) {
  return (
    <div
      className="vi-native-panel"
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--line-soft)',
        padding: 'var(--sp-4)',
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}

/** 页面外壳保留导航和面板职责，新版正文的水平设计留白由蓝图编排。 */
export function PageShell({ variant = 'dashboard', node = null, props = {}, ctx, emit, children, childItems: rawChildItems = [] }) {
  const headerHost = useContext(PageHeaderHostContext);
  const runtime = useViewIR();
  const horizontalInset = bodyHorizontalInset(runtime?.blueprint);
  const layout = props.layout || variant;
  const subpage = layout === 'subpage';
  // The head's two INTERPOLATED lines, while the feed is in flight. `props` has
  // already been resolved against an empty context, so what is here now reads
  // "0 cal logged"; the AUTHORED string is the only place left that still knows
  // a read was spliced in, which is why the node is consulted and not the prop.
  const eyebrowWaits = waitsForData(ctx, node?.props?.eyebrow);
  const subtitleWaits = waitsForData(ctx, node?.props?.subtitle);
  // A `when`-false child is not a child here: the shell draws FURNITURE around
  // its regions (the header actions row, the fixed action bar and the body
  // padding that reserves its height), and an empty bar over a gated button is
  // chrome for nothing.
  const childItems = rawChildItems.filter((c) => !c.hidden);

  // DISCRIMINATED regions. One shell may hold two bodies — `region: "piece"`
  // and `region: "outfit"` — and the FOCUSED row's discriminator field picks
  // which one renders. Rendering both would stack two detail pages. The field
  // is the blueprint's to name (`props.regionField`, default "type"), so the
  // shell carries no domain vocabulary of its own.
  const discriminator = ctx?.focusEntry?.[props.regionField || 'type'] ?? null;
  const discriminated = discriminator != null
    && childItems.some((c) => c.region === discriminator);
  // The header `actions` and the bottom `actionBar` are the shell's own
  // furniture: it draws them, so they must never fall through into the body.
  const inline = childItems.filter((c) => !SHELL_REGIONS.has(c.region) && !(props.headerStyle === 'custom' && c.region === 'header'));
  const actions = childItems.filter((c) => c.region === 'actions');
  const actionBar = childItems.filter((c) => c.region === 'actionBar');
  const bodyItems = discriminated
    ? inline.filter((c) => !c.region || c.region === discriminator)
    : inline;
  // A DASHBOARD lays its modules out as elevated panels (see `wantsPanel`);
  // every other layout keeps the flat stack. `children` is the fallback for a
  // shell rendered directly with React children rather than through the
  // interpreter — there is no child record to read a family off, so there is
  // nothing to panel either.
  // 局部 Padding 不改变 dashboard 子模块的面板身份，面板在留白内部绘制。
  const body = childItems.length
    ? bodyItems.map((c) => {
      if (layout !== 'dashboard') return c.element;
      if (c.type === 'Padding' && c.node?.children?.length === 1) {
        const ref = c.node.children[0];
        const inner = typeof ref === 'string' ? runtime?.blueprint?.components?.[ref] : ref;
        if (wantsPanel({ type: inner?.type, bare: inner?.props?.bare })) {
          return <Padding key={c.id} props={resolveProps(c.node.props || {}, null, ctx)}>
            <Panel>{typeof ref === 'string' ? runtime.renderById(ref) : runtime.renderNode(ref, `${c.id}__child0`)}</Panel>
          </Padding>;
        }
      }
      return wantsPanel(c) ? <Panel key={c.id}>{c.element}</Panel> : c.element;
    })
    : children;

  // 可选头部不会改变旧 JSON 的渲染路径。
  if (usesPageHeader(props.headerStyle)) {
    // 宿主返回优先；独立子页面继续通过既有 backRequested 连线返回。
    const leading = headerHost.leading ?? (subpage ? <button type="button"
      aria-label={props.backLabel || 'Back'}
      onClick={() => emit?.('backRequested', { action: props.backAction || 'back' })}
      style={{ ...resetButton, width: 36, height: 36, borderRadius: 18, border: '1px solid #E3E3E3', background: 'white', color: '#1C1C1E', fontSize: 18 }}>←</button> : null);
    return <PageHeaderHostContext.Provider value={{ ...headerHost, leading }}>
      <PageWithHeader style={props.headerStyle} title={props.title || ctx?.title || ''}
      eyebrow={eyebrowWaits ? null : props.eyebrow} subtitle={subtitleWaits ? null : props.subtitle}
      actions={actions} actionBar={actionBar}
      horizontalInset={horizontalInset}
      headerSafeArea={props.headerSafeArea !== false}
      customHeader={childItems.filter((c) => c.region === 'header').map((c) => c.element)}>{body}</PageWithHeader>
    </PageHeaderHostContext.Provider>;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: '100%', position: 'relative',
      background: 'var(--bg)', color: 'var(--text)',
    }}>
      {/* ADMIN-only diagnostic: which pinned blueprint version is rendering. Only on
          a main (non-detail, non-subpage) page; `ctx.blueprintVersionLabel` is set
          only where an admin/studio caller handed a labeled blueprint, so it never
          surfaces to a normal viewer. Non-interactive, low-contrast, top-right —
          twin of the Flutter PageShell badge. */}
      {ctx?.blueprintVersionLabel && layout !== 'detail' && layout !== 'subpage' ? (
        <div
          data-testid="native-version-badge"
          aria-hidden="true"
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 5, pointerEvents: 'none',
            padding: '3px 8px', borderRadius: 'var(--r-pill)',
            background: 'var(--surface-3)', border: '1px solid var(--line)',
            color: 'var(--text-3)', fontSize: 10, lineHeight: 1.1,
            fontFamily: 'ui-monospace, Menlo, Courier, monospace',
            letterSpacing: '0.2px', opacity: 0.85, maxWidth: '60%',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {/* The standing ADMIN marker (twin of the shared Flutter AdminBadge) —
              makes it obvious this whole badge is admin-only. */}
          <span style={{ fontWeight: 800, letterSpacing: '0.6px', color: '#B26A00' }}>ADMIN</span>
          {' · '}
          {ctx.serverEnv ? `${ctx.serverEnv} · ${ctx.blueprintVersionLabel}` : ctx.blueprintVersionLabel}
        </div>
      ) : null}
      {/* SUBPAGE is the one case the host does NOT own the back chevron: an
          in-bundle sub-view the host never opened has no back stack to pop, so
          the page must offer its own way out. Every other layout keeps the
          seal — the host draws bezel, nav pill, capture button and back. */}
      <header style={{
        padding: subpage ? 'var(--sp-3) var(--sp-4)' : 'var(--sp-5) var(--sp-4) var(--sp-3)',
        display: 'flex', alignItems: subpage ? 'center' : 'flex-end', gap: 'var(--sp-2)',
      }}>
        {subpage ? (
          <button
            type="button"
            aria-label={props.backLabel || 'Back'}
            onClick={() => emit?.('backRequested', { action: props.backAction || 'back' })}
            style={{
              ...resetButton, ...hitTarget,
              flex: '0 0 auto', color: 'var(--text)', fontSize: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >‹</button>
        ) : null}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {eyebrowWaits ? <SkeletonText width="38%" height={10} /> : null}
          {!eyebrowWaits && props.eyebrow ? (
            <div style={{
              fontSize: 'var(--t-xs)', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--accent)',
            }}>{props.eyebrow}</div>
          ) : null}
          <h1 style={{
            margin: 0, fontSize: subpage ? 'var(--t-lg)' : 'var(--t-3xl)',
            lineHeight: subpage ? 1.2 : 1.08, fontWeight: 700,
            letterSpacing: '-0.03em', fontFamily: 'var(--font-display)',
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: subpage ? 'nowrap' : undefined,
          }}>{props.title}</h1>
          {/* The DESCRIPTION line. A title names the widget; this says what it
              is FOR, and it is the first thing a user reads on a page they have
              never opened. The head could carry an eyebrow and a title and had
              nowhere to put the sentence, so every widget's own description
              lived only in the store listing. Measured, not full-bleed: a line
              of prose past ~34 characters stops being read as a caption. */}
          {subtitleWaits ? <SkeletonText width="64%" height={11} /> : null}
          {!subtitleWaits && props.subtitle ? (
            <p style={{
              margin: 0, marginTop: 'var(--sp-1)', fontSize: 'var(--t-sm)',
              color: 'var(--text-3)', lineHeight: 1.35, maxWidth: '34ch',
            }}>{props.subtitle}</p>
          ) : null}
        </div>
        {actions.length ? (
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {actions.map((c) => c.element)}
          </div>
        ) : null}
      </header>
      <div style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        gap: LAYOUT_GAP[layout] || 'var(--sp-4)',
        paddingTop: 0,
        paddingLeft: layout === 'full-bleed' ? 0 : horizontalInset,
        paddingRight: layout === 'full-bleed' ? 0 : horizontalInset,
        paddingBottom: layout === 'full-bleed' ? 0 : 'var(--sp-6)',
        // an action bar is FIXED over the body, so the body reserves its height
        // rather than hiding its own last row underneath it
        ...(actionBar.length ? { paddingBottom: 96 } : {}),
        overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none',
        // the phone SWALLOWS the wheel: without this, hitting the top or the
        // bottom of this body chains the scroll to whatever page the phone is
        // embedded in (the studio strip), so the widget appears to jump away
        // under the pointer. Passive-safe — no wheel handler, no preventDefault.
        overscrollBehavior: 'contain',
        position: 'relative',
      }}>
        {body}
      </div>
      {actionBar.length ? (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 20,
          padding: 'var(--sp-3) var(--sp-4) var(--sp-5)',
          display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
          background: 'color-mix(in srgb, var(--bg) 86%, transparent)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid var(--line-soft)',
        }}>
          {actionBar.map((c) => c.element)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Internal / structural keys a `mode: "rest"` table must never print: they are
 * identity, lifecycle and DERIVE ARTIFACTS, not facts about the thing.
 *
 * The third category is the one that kept leaking. A rest table prints
 * `Object.keys(row)`, and the row a detail page focuses on has been through the
 * derive layer — so `cover`, `media_state`, `sticker_resolved`, `safety_rank`,
 * `version_count`, `group_key`, `top`, `latest` are all sitting on it, and a
 * page ended up showing the user "Cover: https://…" and "Has session: true" as
 * if they were facts about their plant. Five of the nine blueprints answered
 * that with a hand-written `exclude[]` naming every artifact their own derives
 * happened to emit — which is the framework's list, written nine times, always
 * one derive behind.
 *
 * Patterns, not names, because the artifacts are NAMED BY A PATTERN: a derive's
 * `as` is an author's word (`sticker`, `safety`, `clip`) and only the SUFFIX is
 * the runtime's (`_state`, `_resolved`, `_tone`, `_label`, `_rank`, `_open`,
 * `_count`).
 *
 * This governs `mode:"rest"` ONLY. A page that genuinely wants one of these in
 * a table names it in `fields[]`, which is never filtered — hiding is the
 * default, not a ban.
 */
const KV_HIDDEN = new RegExp(`^(${[
  // identity & provenance
  'id', '.*_id', 'key', '.*_key', 'client_ref', 'kind', 'type', 'op', 'action',
  // time
  '.*_at',
  // media handles, and the tri-state a `mediaState`-shaped derive emits for one
  '.*_url', 'images', 'photos', 'slides', 'cover', '.*_state', '.*_resolved',
  // gallery / append bookkeeping — a list of media:// refs + its by-index log-card
  // map; NEVER user-facing (a rest table printed these back at the user)
  'image_cards.*',
  // presentation-ready fields a derive emits for a card to bind blindly
  '.*_tone', '.*_label', '.*_rank', '.*_open',
  // grouping / lifecycle bookkeeping
  'entries', 'items', 'payload', 'top', 'latest', 'root', 'count', '.*_count',
  'deleted', 'removed', 'session_deleted', 'has_session', 'is_sample', 'edited', 'orphan',
  // Job-by-Contract lifecycle bookkeeping — stamped on a contract-keyed row by
  // widget_capture_entry / job_contract AFTER the blueprints authored their
  // exclude[]: the raw status enum, the derived contract region, the media slot
  // id, the capture provenance, the contract uid + its recovery attempts. Pure
  // machine internals a rest table must never print (the sibling `in_progress_at`
  // / `source_updated_at` / `source_session_id` already fall under `.*_at` /
  // `.*_id`). A page that genuinely wants one names it in `fields[]`.
  'status', 'state', 'region', 'slot', '.*_slot', 'capture_source', '.*_uid', 'attempts',
].join('|')})$`);

/**
 * One `{type:'kv', fields:[…]}` section — the detail page's data table.
 *
 * `mode: "rest"` is the other half: print whatever the row carries that the
 * page has not already shown, minus `exclude[]`. A model-authored row grows
 * fields the blueprint never anticipated, and a fixed `fields[]` silently
 * drops them — which is how a detail page ends up hiding the one field the
 * user cared about.
 */
function KeyValues({ item, fields, mode, exclude, labels }) {
  const skip = new Set([...(exclude || []), ...(mode === 'rest' ? (fields || []) : [])]);
  const keys = mode === 'rest'
    ? Object.keys(item || {}).filter((k) => !skip.has(k) && !KV_HIDDEN.test(k))
    : (fields || []).filter((k) => !skip.has(k));
  // A cell is LINES, not a string: an object field is a small table, and
  // `JSON.stringify` printed one at the user (nutritionist's `safety` rendered
  // as `{"verdict":"caution",…}` on the page). `labels` decodes the enums the
  // row stores in the model's vocabulary — see `kvLines`.
  const rows = keys
    .map((f) => [f, kvLines(item?.[f], { labels, field: f, exclude: [...skip] })])
    .filter(([, lines]) => lines.length);
  if (!rows.length) return null;
  return (
    <dl style={{
      margin: 0, background: 'var(--surface)', borderRadius: 'var(--r)',
      padding: '2px var(--sp-4)', boxShadow: 'var(--shadow-1)',
    }}>
      {rows.map(([k, lines], i) => (
        <div key={k} style={{
          display: 'flex', gap: 'var(--sp-3)', padding: '10px 0',
          borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
        }}>
          <dt style={{
            flex: '0 0 40%', fontSize: 'var(--t-sm)', color: 'var(--text-3)',
            textTransform: 'capitalize',
          }}>{k.replace(/_/g, ' ')}</dt>
          <dd style={{
            margin: 0, flex: 1, minWidth: 0, fontSize: 'var(--t-sm)', color: 'var(--text)',
            wordBreak: 'break-word', display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {lines.map((l, n) => (
              <span key={`${l.label || 'l'}-${n}`} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
                {l.label ? (
                  <span style={{ color: 'var(--text-3)', flex: '0 0 auto' }}>{l.label}</span>
                ) : null}
                <span style={{ minWidth: 0 }}>{l.text}</span>
              </span>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The media a page section falls back to when it declares no component. */
function mediaOf(item) {
  const list = item?.images || item?.photos || item?.slides;
  if (Array.isArray(list) && list.length) return list;
  const one = item?.image_url || item?.photo_url;
  return one ? [one] : [];
}

/**
 * One declared section, in the COLLAPSED grammar (report F4, README §4.4).
 *
 * A section is a place to PUT a component, and that is the DEFAULT: `{id?,
 * component}` with no `type` at all, rendered as a real node with its own id
 * and `emit`. It used to need a keyword, and since the vocabulary was closed at
 * `hero|carousel|gallery|kv|action`, every custom section had to lie about its
 * type — authors wrote `{"type":"action", component}` for a chart, a notice
 * strip, a download row. 63 of 73 sections carried a `component`, so the
 * keyword was noise on all but ten of them.
 *
 * What KEEPS a keyword is the section that renders itself from the focused row:
 *   kv        the data table — the one that genuinely has no component
 *   hero      the row's own photo
 *   carousel  the row's own media, as a Carousel
 *   gallery   the row's own media, as a MediaPicker
 * `node`/`section`/`action` are the retired keywords; a type-less `{component}`
 * is what they all meant. An unknown TYPE renders the fail-closed marker: a
 * page that silently drops a section it did not understand is how a detail view
 * ships with a missing half.
 *
 * `emit` is the sugar's other half. The default renderers are real components
 * with real signals (a gallery's `addRequested`, an image's `tapped`), and
 * rendering them without an emit made those signals unreachable — a gallery
 * that could not ask the host to pick a photo. They fire under the PAGE node's
 * id, under their own signal names: a default section has no id, so its
 * declared parent is its wiring (the same rule a card template and a `repeat`
 * row follow).
 */
function Section({ section, item, ctx, emit, render }) {
  // A `component` IS the section. It needs no `type` and it OVERRIDES one: a
  // section that names a component and renders nothing (a `when` that gated it
  // away) must draw nothing, never fall through to a keyword's default body.
  if (section.component !== undefined) return render(section.component);

  switch (section.type) {
    case 'hero': {
      const src = item?.image_url || item?.photo_url || null;
      if (!src) return null;
      return <Image props={{ src, fit: section.fit || 'cover', ratio: section.ratio || '3/4' }} emit={emit} />;
    }
    case 'carousel': {
      // the lowercase `carousel` is the SECTION keyword; `Carousel` is the
      // component it renders. They now share a name because they always were
      // the same thing — a keyword and a near-copy of the paged slide track.
      const slides = mediaOf(item);
      if (!slides.length && !ctx?.focusGrid?.photo_url) return null;
      return (
        <Carousel
          props={{ slides, dots: section.dots, thumbs: section.thumbs, ratio: section.ratio, fit: section.fit }}
          ctx={ctx} emit={emit}
        />
      );
    }
    case 'gallery':
      return <MediaPicker props={{ items: mediaOf(item), max: section.max, add: section.add }} emit={emit} />;
    case 'kv':
      return (
        <KeyValues
          item={item} fields={section.fields} mode={section.mode}
          exclude={section.exclude} labels={section.labels}
        />
      );
    case 'action':
    case 'node':
    case 'section':
      // The retired passthrough keywords, reached with nothing to pass through.
      // That is an authoring error, not an absent section — say so rather than
      // drawing a gap.
      return (
        <UnsupportedComponent
          what="section" name={section.type}
          detail="a section needs `component`: an inline node, or the id of a declared one (and no `type` at all)"
        />
      );
    default:
      return <UnsupportedComponent what="section" name={section.type || '(untyped)'} detail="a section is `{component}`; the self-rendering ones are kv | hero | carousel | gallery" />;
  }
}

function SectionList({ sections, item, ctx, emit, render, ownerId, children }) {
  const list = Array.isArray(sections) ? sections : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {list.map((s, i) => {
        const sec = s || {};
        // An INLINE section node is ADDRESSABLE: `id` is the name a connection
        // wires (`"from": "docActions.activated"`), and without one it gets a
        // stable owner-scoped key so two pages can't collide. A section that
        // references a declared component keeps that component's own id.
        const nodeKey = sec.id || `${ownerId || 'page'}__section${i}`;
        const content = <Section key={nodeKey} section={sec} item={item} ctx={ctx} emit={emit}
          render={(ref) => render(ref, nodeKey)} />;
        // 内置区块没有组件引用，使用同一 JSON 四边内距语义。
        return sec.padding ? <div key={nodeKey} className="vi-native-padding" style={contentPadding(sec.padding)}>{content}</div> : content;
      })}
      {children}
    </div>
  );
}

/** The graceful degrade both pages owe a capture the user already deleted. */
// 内置提示的外部留白由详情页 JSON 指定；缺省为零，兼容旧 bundle。
function SessionDeletedNote({ bodyPadding }) {
  return (
    <Padding props={bodyPadding}>
      <div style={{
        fontSize: 'var(--t-xs)', color: 'var(--text-3)', padding: 'var(--sp-3)',
        background: 'var(--surface-2)', borderRadius: 'var(--r-sm)',
      }}>
        The original session was deleted — chat actions fall back to the main session.
      </div>
    </Padding>
  );
}

/**
 * A focused row that is GONE, in the two ways it can be gone.
 *
 * Either the id resolved to nothing (a deep link to a deleted item, a share
 * opened after the owner removed it), or the FOLD handed back a tombstone — the
 * row is still in the feed carrying `deleted` / `removed`, which is how a
 * delete propagates before the next full fetch. Both are the same fact for the
 * reader: this item is not here any more.
 */
function isMissing(item) {
  if (!item) return true;
  if (typeof item !== 'object') return false;
  if (item.deleted === true || item.removed === true) return true;
  return Object.keys(item).length === 0;
}

// Job-by-Contract V3e — the KIT-side friendly text for the contract PROGRESS card (enum →
// human word, mirrored in Flutter, never i18n — same as the "Private" chip / EmptyState
// copy). `step` is the job_type family (unknown → DROPPED, never printed raw). `reason` is
// one of SIX fixed failure CODES; `contract_lifecycle` drives the header word.
const PROGRESS_STEP_LABEL = { image: 'Image', video: 'Video', music: 'Music', log: 'Saved', worker: 'Task', file: 'File' };
const PROGRESS_LIFECYCLE_HEADER = {
  queued: 'Queued', running: 'Generating…', delivered: 'Delivered',
  error: "Couldn't finish", stopped: 'Stopped',
};
const PROGRESS_STATUS_TEXT = { delivered: 'Delivered', running: 'Generating…', pending: 'Queued' };
const PROGRESS_REASON_TEXT = {
  image_gen_failed: "Image couldn't be generated",
  video_gen_failed: "Video couldn't finish",
  music_gen_failed: "Music couldn't finish",
  worker_crash: 'Task failed',
  insufficient_credits: 'Out of credits',
  generic: "Couldn't finish",
};

/** ISO-8601 → epoch ms, or null (absent / unparseable). */
function progressMs(iso) { const t = iso ? Date.parse(iso) : NaN; return Number.isNaN(t) ? null : t; }
/** A non-negative span in ms → `m:ss` (or `h:mm:ss`); null for absent / negative. */
function fmtElapsed(ms) {
  if (ms == null || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  const hh = Math.floor(s / 3600); const mm = Math.floor((s % 3600) / 60); const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}
/** A leg's timing: finished → its duration (ended−started); running → live (now−started); else none. */
function legElapsed(s, now) {
  const start = progressMs(s.started_at);
  if (start == null) return null;
  const end = progressMs(s.ended_at);
  return fmtElapsed((end == null ? now : end) - start);
}
/** The one status line a step row shows — a failure REASON code → friendly text, a blocked
 * leg → "Waiting for <upstream>", else the plain status word. Never a raw code / id / error. */
function progressStatusText(s) {
  if (s.status === 'failed') return PROGRESS_REASON_TEXT[s.reason] || 'Failed';
  if (s.status === 'blocked') {
    const w = PROGRESS_STEP_LABEL[s.waiting_on];
    return w ? `Waiting for ${w}` : 'Waiting…';
  }
  return PROGRESS_STATUS_TEXT[s.status] || '';
}

/** A 1s tick that runs ONLY while the contract is active — so a running leg's elapsed ticks
 * up between SSE refreshes; the interval is cleared on settle / unmount (reduced-motion is
 * handled by the breathing-dot CSS, not here). */
function useProgressNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** One step's glyph — the card/badge visual language: ✓ delivered · ✗ failed · ⊘ blocked · breathing dot running · grey dot pending. */
function ProgressStepGlyph({ status }) {
  const glyph = { delivered: '✓', failed: '✗', blocked: '⊘' }[status];
  if (glyph) {
    const color = status === 'delivered' ? 'var(--good)' : status === 'failed' ? 'var(--bad)' : 'var(--text-3)';
    return <span aria-hidden style={{ color, fontWeight: 700, fontSize: 12, lineHeight: 1, width: 12, textAlign: 'center' }}>{glyph}</span>;
  }
  const running = status === 'running';
  return (
    <span
      aria-hidden
      style={{
        width: 8, height: 8, borderRadius: 4, background: running ? 'var(--good)' : 'var(--text-3)',
        ...(running ? { animation: 'viNativeBreath 1.6s ease-in-out infinite' } : null),
      }}
    />
  );
}

/**
 * KIT DEFAULT (Job-by-Contract V3e): the session-bound detail page's PROGRESS card — a
 * per-step TIMELINE. The focus entry carries `contract_progress` (`[{step,status,reason?,
 * waiting_on?,started_at?,ended_at?}]`) + `contract_started_at`/`contract_settled_at`. Draw a
 * header (`contract_lifecycle` word + running dot + total elapsed) and one rail row per step:
 * glyph · step label · status (Delivered / Generating… / Queued / failure-reason / "Waiting
 * for <upstream>") · timing. `delivered` renders NOTHING (the hero IS the delivery). Elapsed
 * ticks live (1s) while active, computed from the timestamps (never a value frozen at fetch).
 * NEVER prints raw ids / slots / refs / urls / error text (no-leak; unknown step/reason drop
 * to safe defaults). Absent → nothing (BC). The Flutter twin is `viewIrContractProgressCard`.
 */
// 内置提示的外部留白由详情页 JSON 指定；缺省为零，兼容旧 bundle。
function ContractProgressCard({ item, bodyPadding }) {
  const lifecycle = contractLifecycle(item);
  const active = lifecycle === 'queued' || lifecycle === 'running';
  const now = useProgressNow(active);
  const steps = Array.isArray(item?.contract_progress)
    ? item.contract_progress.filter((s) => s && PROGRESS_STEP_LABEL[s.step])
    : [];
  // A finished creation needs no status furniture — the hero IS the delivery. Show the
  // timeline only for the ACTIVE/terminal-unfulfilled states; `delivered` renders nothing.
  // A lifecycle that can't be resolved but still carries steps falls through to shown (BC).
  if (!steps.length || lifecycle === 'delivered') return null;
  const header = PROGRESS_LIFECYCLE_HEADER[lifecycle] || null;
  const startMs = progressMs(item && item.contract_started_at);
  const settledMs = progressMs(item && item.contract_settled_at);
  const total = startMs == null ? null : fmtElapsed((settledMs == null ? now : settledMs) - startMs);
  return (
    <Padding props={bodyPadding}>
      <div
        data-vi-contract-progress="1"
        style={{
          background: 'var(--surface)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-1)',
          padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
        }}
      >
        {header ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text)' }}>
            {lifecycle === 'running' ? (
              <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--good)', animation: 'viNativeBreath 1.6s ease-in-out infinite' }} />
            ) : null}
            <span style={{ flex: 1, minWidth: 0 }}>{header}</span>
            {total ? <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', fontWeight: 600 }}>{total}</span> : null}
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {steps.map((s, i) => {
            const timing = legElapsed(s, now);
            const last = i === steps.length - 1;
            return (
              <div
                key={`${s.step}-${i}`}
                data-vi-progress-step={s.step}
                style={{ display: 'flex', alignItems: 'stretch', gap: 'var(--sp-3)', fontSize: 'var(--t-sm)', color: 'var(--text-2)', paddingBottom: last ? 0 : 'var(--sp-2)' }}
              >
                {/* rail: the glyph + a connector line down to the next row */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12 }}>
                  <span style={{ height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ProgressStepGlyph status={s.status} /></span>
                  {last ? null : <span style={{ flex: 1, width: 1, minHeight: 8, background: 'var(--line-soft)' }} />}
                </div>
                <span style={{ flex: '0 0 60px', color: 'var(--text)', lineHeight: '18px' }}>{PROGRESS_STEP_LABEL[s.step]}</span>
                <span style={{ flex: 1, minWidth: 0, lineHeight: '18px' }}>{progressStatusText(s)}</span>
                {timing ? <span style={{ flex: '0 0 auto', fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', lineHeight: '18px' }}>{timing}</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    </Padding>
  );
}

/**
 * ItemPage — the host-opened per-item page (data_view), bound to
 * `page_args.item_id` → `focusEntry`. SEALED: the host owns the back chrome,
 * edits land in place, and the section grammar is DECLARED, so a second widget
 * gets a detail page by listing sections instead of authoring a page.
 *
 * `missingState` is the page's answer for a focus that resolved to NOTHING.
 * `EmptyState variant:"not-found"` has always been the right render for it —
 * the widget is not empty, that one item is gone — and the page had no way to
 * reach it, so a stale deep link showed either a developer note about
 * `page_args.item_id` or a detail page of blanks. Declare
 * `{title, body?, cta?}` (or just `true`) and the whole body is replaced.
 * Absent it, the developer note stands: an author who has not thought about
 * this case should SEE that they have not.
 */
export function ItemPage({ nodeId, props = {}, data, ctx, emit, renderById, renderNode }) {
  const item = props.focus || data || ctx?.focusEntry || null;
  const render = (ref, key) => (typeof ref === 'string'
    ? renderById?.(ref)
    : (ref ? renderNode?.(ref, key) : null));

  const missing = props.missingState;
  if (missing && isMissing(item)) {
    const spec = typeof missing === 'object' ? missing : {};
    return (
      <Padding props={props.bodyPadding}>
        <EmptyState
          variant="not-found"
          props={{
            kind: 'not-found',
            title: spec.title || 'This item is no longer here',
            body: spec.body,
            cta: spec.cta,
          }}
          ctx={ctx}
          emit={emit}
        />
      </Padding>
    );
  }

  if (!item) {
    return (
      <Padding props={props.bodyPadding}>
        <div style={{ padding: 'var(--sp-5)', color: 'var(--text-3)', fontSize: 'var(--t-sm)' }}>
          No item selected. The host opens this page with `page_args.item_id`.
        </div>
      </Padding>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <ContractProgressCard item={item} bodyPadding={props.bodyPadding} />
      <SectionList sections={props.sections} item={item} ctx={ctx} emit={emit} render={render} ownerId={nodeId}>
        {item.session_deleted ? <SessionDeletedNote bodyPadding={props.bodyPadding} /> : null}
      </SectionList>
    </div>
  );
}

/**
 * SessionPage — the host-opened per-CAPTURE page, bound to
 * `page_args.session_id` → `focusGrid`. Same section grammar; the carousel
 * defaults to the session's own media (the Carousel leads with the original
 * source photo and dedups it once), and a deleted session degrades instead of
 * dead-ending.
 */
export function SessionPage({ nodeId, props = {}, data, ctx, emit, renderById, renderNode }) {
  const focus = props.focus || data || ctx?.focusGrid || null;
  const render = (ref, key) => (typeof ref === 'string'
    ? renderById?.(ref)
    : (ref ? renderNode?.(ref, key) : null));

  if (!focus) {
    return (
      <Padding props={props.bodyPadding}>
        <div style={{ padding: 'var(--sp-5)', color: 'var(--text-3)', fontSize: 'var(--t-sm)' }}>
          No capture selected. The host opens this page with `page_args.session_id`.
        </div>
      </Padding>
    );
  }

  const sections = Array.isArray(props.sections) && props.sections.length
    ? props.sections
    : [{ type: 'carousel' }];

  return (
    <SectionList sections={sections} item={focus} ctx={ctx} emit={emit} render={render} ownerId={nodeId}>
      {focus.session_deleted ? <SessionDeletedNote bodyPadding={props.bodyPadding} /> : null}
    </SectionList>
  );
}
